import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  TextInput,
  Modal,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle, G } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import {
  PageContainer,
  Navbar,
  Breadcrumb,
  CommentSection,
  CommentData,
  getAvatarColorFromUserType,
  UserType,
} from '../../components';
import TargetAchievementCard from '../../components/TargetAchievementCard';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { servicesApi, kpisApi, goalsApi, serviceCommentsApi, ServiceComment as ApiServiceComment } from '../../services/apiService';
import { Service, KPIList } from '../../types';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { useCachedData } from '../../hooks/useCachedData';
import { useRoleNavigation } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';

type RouteProps = RouteProp<MainStackParamList, 'ServiceDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ExportIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 8L12 3L7 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 3V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ArrowRightIcon = ({ size = 16, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M5 12H19M19 12L12 5M19 12L12 19"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// ============================================================
// Donut Chart Component
// ============================================================

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

/**
 * Get color based on achievement percentage
 * - >= 100%: Green (#22C55E) - ممتاز
 * - >= 80% and < 100%: Orange (#F97316) - جيد
 * - < 80%: Red (#EF4444) - يحتاج تحسين
 */
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return '#22C55E';  // Green - Excellent
  if (percentage >= 80) return '#F97316';   // Orange - Good
  return '#EF4444';                          // Red - Needs Improvement
};

const DonutChart = ({ percentage, size = 168, strokeWidth = 12 }: DonutChartProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  const progressColor = getAchievementColor(percentage);

  // Calculate dot position at the end of progress arc
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#E2E8F0"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot indicator at end of progress */}
        <Circle
          cx={dotX}
          cy={dotY}
          r={7.5}
          fill={progressColor}
          stroke="#FCFCFC"
          strokeWidth={2}
        />
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: 20, fontFamily: FONTS.numbers, fontWeight: '500', color: '#414042' }}>
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Pie Chart Component (Status Distribution)
// ============================================================

interface PieChartProps {
  data: { value: number; color: string; label: string }[];
  size?: number;
}

const PieChart = ({ data, size = 100 }: PieChartProps) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const center = size / 2;
  const outerRadius = size / 2 - 5;
  const innerRadius = outerRadius * 0.6; // Donut hole

  if (total === 0) {
    return (
      <Svg width={size} height={size}>
        <Circle cx={center} cy={center} r={outerRadius} fill="#E2E8F0" />
        <Circle cx={center} cy={center} r={innerRadius} fill="white" />
      </Svg>
    );
  }

  let currentAngle = -90;

  const paths = data.map((item, index) => {
    const angle = (item.value / total) * 360;

    // Full circle: SVG arc can't draw 360°, use Circle instead
    if (angle >= 359.99) {
      return (
        <G key={index}>
          <Circle cx={center} cy={center} r={outerRadius} fill={item.color} />
        </G>
      );
    }

    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const outerX1 = center + outerRadius * Math.cos(startRad);
    const outerY1 = center + outerRadius * Math.sin(startRad);
    const outerX2 = center + outerRadius * Math.cos(endRad);
    const outerY2 = center + outerRadius * Math.sin(endRad);
    const innerX1 = center + innerRadius * Math.cos(startRad);
    const innerY1 = center + innerRadius * Math.sin(startRad);
    const innerX2 = center + innerRadius * Math.cos(endRad);
    const innerY2 = center + innerRadius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    const d = [
      `M ${outerX1} ${outerY1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerX2} ${outerY2}`,
      `L ${innerX2} ${innerY2}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerX1} ${innerY1}`,
      'Z',
    ].join(' ');

    return <Path key={index} d={d} fill={item.color} />;
  });

  return (
    <Svg width={size} height={size}>
      <G>{paths}</G>
      <Circle cx={center} cy={center} r={innerRadius} fill="white" />
    </Svg>
  );
};


// ============================================================
// Main Component
// ============================================================

export default function ServiceDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id } = route.params;
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, canComment, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  // DTS 2.2: only Strategy Team (hrcpms.soft_delete) can delete operational
  // items. Section Head has create/edit but no delete.
  const canSoftDelete = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.soft_delete')
  );
  const { width } = useWindowDimensions();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
  };

  // Responsive breakpoints
  const isSmallScreen = width < 768;

  const { data: service, setData: setService, loading, setLoading } = useCachedData<Service>(`service-${id}`);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';
  const canApproveDeletion = (user?.is_superuser || user?.position_type === 'MANAGER' || user?.position_type === 'DIRECTOR' || (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false));

  // Comments state
  const [apiComments, setApiComments] = useState<ApiServiceComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [editingComment, setEditingComment] = useState<CommentData | null>(null);


  // Convert API comment to display format (unified with GoalDetailScreen pattern)
  const convertApiComment = (apiComment: ApiServiceComment): CommentData => {
    // Get the actual author name from author_details
    const fullName = apiComment.author_details?.full_name || apiComment.author_details?.username || t('common.user');
    const nameParts = fullName.split(' ').filter(Boolean);
    const authorName = nameParts[0] || t('common.user'); // First name only

    // Generate initials from the actual name
    const initials = nameParts.length >= 2
      ? `${nameParts[0][0]} ${nameParts[1][0]}`
      : fullName.slice(0, 2);

    const isOwner = user?.id === apiComment.author;
    const userType = (apiComment.author_type || 'org_employee') as UserType;
    const avatarColor = getAvatarColorFromUserType(userType);

    return {
      id: apiComment.id,
      name: authorName,
      initials,
      date: new Date(apiComment.created_at).toLocaleDateString('ar-SA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      text: apiComment.content,
      avatarColor,
      showMenu: isOwner, // Only show menu for own comments
      isEdited: apiComment.is_edited,
      originalText: apiComment.original_content,
      userType,
    };
  };

  // Convert API comments to display format
  const comments = apiComments.map(convertApiComment);

  // Fetch service comments
  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const commentsData = await serviceCommentsApi.getForService(id);
      setApiComments(commentsData);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  // Fetch service details
  const fetchService = useCallback(async () => {
    try {
      if (!service) setLoading(true);
      const data = await servicesApi.get(id);
      setService(data);

      // Use KPIs from service response (already filtered consistently with kpi_distribution)
      // This ensures the KPIs list matches the distribution data
      setKpis(data.kpis || []);

      // Fetch comments for this service
      fetchComments();
    } catch (error) {
      console.error('Error fetching service:', error);
      showError(t('services.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, showError, fetchComments]);

  // Auto-refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      fetchService();
    }, [fetchService])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchService();
  }, [selectedYear]);

  // Comment handlers - unified with GoalDetailScreen pattern
  const handleAddComment = async (text: string) => {
    try {
      // Determine author_type based on user profile
      const authorType = (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false) ? 'strategy_team' : 'org_employee';

      await serviceCommentsApi.create({
        service: id,
        content: text,
        comment_type: 'note',
        author_type: authorType,
      });
      showSuccess(t('comments.addSuccess'));
      setShowAddComment(false);
      fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      showError(t('comments.addFailed'));
    }
  };

  const handleEditComment = (comment: CommentData) => {
    setEditingComment(comment);
    setShowAddComment(true);
  };

  const handleUpdateComment = async (text: string) => {
    if (!editingComment) return;
    try {
      await serviceCommentsApi.update(editingComment.id, { content: text });
      showSuccess(t('comments.updateSuccess'));
      setEditingComment(null);
      setShowAddComment(false);
      fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
      showError(t('comments.updateFailed'));
    }
  };

  // Print/export ONLY the service report — not the whole app page. We build an
  // isolated HTML document (just this service's info + KPIs) and print that in a
  // detached window, mirroring the SubProcess detail print. Printing the live
  // page would drag in the sidebar/navbar and force a slow full re-render.
  const handleExportPDF = () => {
    if (!service) {
      showError(t('common.noDataToExport'));
      return;
    }

    const detailRows: Array<[string, string | undefined | null]> = [
      [t('services.serviceFees'), service.service_fees],
      [t('services.requirementsConditions'), service.requirements_conditions],
      [t('services.stepsToObtain'), service.steps_to_obtain],
      [t('services.deliveryChannels'), service.delivery_channels],
      [t('services.targetCustomers'), service.target_customers],
      [t('services.serviceLimitations'), service.service_limitations],
      [t('services.serviceHours'), service.service_hours],
      [t('services.additionalInfo'), service.additional_info],
    ];
    const detailsHTML = detailRows
      .filter(([, value]) => !!value)
      .map(([label, value]) => `
        <div class="section">
          <h2 class="section-title">${label}</h2>
          <div class="description">${value}</div>
        </div>
      `).join('');

    const kpisHTML = kpis.length > 0
      ? kpis.map(kpi => `
          <tr>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: center;">${kpi.achievement_percentage || 0}%</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${kpi.target_value || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${kpi.measurement_frequency || '-'}</td>
            <td style="padding: 12px; border: 1px solid #E5E7EB; text-align: right;">${kpi.name}</td>
          </tr>
        `).join('')
      : `<tr><td colspan="4" style="padding: 20px; text-align: center; color: #6B7280;">${t('kpis.noKPIs')}</td></tr>`;

    const htmlContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>${t('services.serviceDetails')} - ${translate(service.name)}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 40px;
            background: #fff;
            color: #1F2937;
            direction: rtl;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #A9211F;
          }
          .header h1 { color: #A9211F; font-size: 24px; margin-bottom: 8px; }
          .header p { color: #6B7280; font-size: 14px; }
          .section { margin-bottom: 30px; }
          .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #1F2937;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 1px solid #E5E7EB;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-bottom: 20px;
          }
          .info-item {
            background: #F9FAFB;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #E5E7EB;
          }
          .info-label { font-size: 12px; color: #6B7280; margin-bottom: 4px; }
          .info-value { font-size: 16px; font-weight: 600; color: #1F2937; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; }
          th {
            background: #F3F4F6;
            padding: 12px;
            border: 1px solid #E5E7EB;
            text-align: right;
            font-weight: 600;
            color: #374151;
          }
          .description {
            background: #F9FAFB;
            padding: 16px;
            border-radius: 8px;
            border: 1px solid #E5E7EB;
            line-height: 1.8;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #E5E7EB;
            text-align: center;
            color: #9CA3AF;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${t('services.serviceDetails')}</h1>
          <p>${translate(service.name)}</p>
        </div>

        <div class="section">
          <h2 class="section-title">${t('common.basicInfo')}</h2>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">${t('services.serviceName')}</div>
              <div class="info-value">${translate(service.name)}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('organizations.organizationalUnit')}</div>
              <div class="info-value">${translate(service.owning_unit_name) || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('goals.strategicGoal')}</div>
              <div class="info-value">${translate(service.strategic_goal_title) || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('kpis.achievementPercentage')}</div>
              <div class="info-value">${service.achievement_percentage != null ? service.achievement_percentage + '%' : '-'}</div>
            </div>
          </div>
        </div>

        ${service.description ? `
        <div class="section">
          <h2 class="section-title">${t('common.description')}</h2>
          <div class="description">${translate(service.description)}</div>
        </div>
        ` : ''}

        ${detailsHTML}

        <div class="section">
          <h2 class="section-title">${t('kpis.performanceIndicators')} (${kpis.length})</h2>
          <table>
            <thead>
              <tr>
                <th style="width: 100px;">${t('kpis.achievementPercentage')}</th>
                <th>${t('kpis.targetValue')}</th>
                <th>${t('kpis.measurementFrequency')}</th>
                <th>${t('kpis.kpiName')}</th>
              </tr>
            </thead>
            <tbody>
              ${kpisHTML}
            </tbody>
          </table>
        </div>

        <div class="footer">
          <p>${t('common.exportedOn')}: ${new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p>${t('common.appName')}</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } else {
      showError(t('common.printWindowError'));
    }
  };

  const handleDelete = () => {
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await servicesApi.delete(id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('services.deleteSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        navigation.goBack();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('messages.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const response = await serviceCommentsApi.delete(commentId);
      // Check if it's a pending delete request (org_employee)
      showSuccess(t('comments.deleteSuccess'));
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      showError(t('comments.deleteFailed'));
    }
  };

  const handleCommentSubmit = (text: string) => {
    if (editingComment) {
      handleUpdateComment(text);
    } else {
      handleAddComment(text);
    }
  };

  const handleCommentCancel = () => {
    setEditingComment(null);
    setShowAddComment(false);
  };

  // Use kpi_distribution from API response instead of local calculation
  const distribution = service?.kpi_distribution ?? { excellent: 0, good: 0, needs_improvement: 0 };
  const hasDistributionData = (distribution.excellent ?? 0) > 0 || (distribution.good ?? 0) > 0 || (distribution.needs_improvement ?? 0) > 0;

  const pieData = hasDistributionData ? [
    { value: distribution.excellent ?? 0, color: '#2ca02c', label: t('status.excellent') },
    { value: distribution.good ?? 0, color: '#ff7f0e', label: t('status.good') },
    { value: distribution.needs_improvement ?? 0, color: '#d62728', label: t('status.needsImprovement') },
  ].filter(item => item.value > 0) : [
    { value: 1, color: '#E2E8F0', label: t('common.noData') },
  ];

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  if (!service) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{t('services.notFound')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.content, isSmallScreen && styles.contentSmall]}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('services.title'), route: 'Services' },
            { label: t('services.serviceDetails') },
          ]}
        />

        {/* Header Section */}
        <View style={[styles.headerSection, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={styles.headerRow}>
            <View style={styles.headerActions}>
              <TouchableOpacity style={styles.actionButton} onPress={handleExportPDF}>
                <Text style={styles.actionButtonText}>{t('common.exportPDF')}</Text>
                <ExportIcon size={13} color={themeColors.iconDefault} />
              </TouchableOpacity>
              {/* Hide edit/delete buttons for view-only users */}
              {!isViewOnly && (
                <>
                  {canSoftDelete && (
                    <TouchableOpacity
                      style={[styles.actionButton, { backgroundColor: '#FEE2E2', borderColor: '#FECACA' }]}
                      onPress={handleDelete}
                    >
                      <Text style={{ fontSize: 13, color: '#DC2626', fontFamily: FONTS.medium }}>{t('common.delete')}</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => navigation.navigate('AddService', { serviceId: id })}
                  >
                    <Text style={styles.actionButtonText}>{t('common.edit')}</Text>
                    <EditIcon size={13} color={themeColors.iconDefault} />
                  </TouchableOpacity>
                </>
              )}
            </View>
            <View style={styles.headerContent}>
              <Text style={[styles.pageTitle, { color: themeColors.textPrimary }]}>{translate(service.name)}</Text>
              <Text style={[styles.pageDescription, { color: themeColors.textMuted }]}>{translate(service.description)}</Text>
            </View>
          </View>
        </View>

        {/* Pending Deletion Banner */}
        {(service as any).pending_deletion_info && (
          <DeletionApprovalBanner
            deletionInfo={(service as any).pending_deletion_info}
            canApprove={canApproveDeletion}
            canCancel={isOrgEmployee && (service as any).pending_deletion_info.requested_by_id === user?.id}
            onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
            onRejected={() => { showSuccess(t('deletion.rejected')); fetchService(); }}
            onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchService(); }}
            onViewImpact={() => setShowDeletionImpact(true)}
          />
        )}

        {/* Stats Cards Row */}
        <View style={[styles.statsRow, isSmallScreen && styles.statsRowSmall]}>
          {/* Basic Information Card - RIGHT */}
          <View style={[styles.statsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.statsCardTitle, { color: themeColors.textPrimary }]}>{t('common.basicInfo')}</Text>
            <View style={styles.infoItemsContainer}>
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>{t('organizations.organizationalUnit')}</Text>
                <Text style={styles.infoValue}>
                  {translate(service.owning_unit_name) || t('common.notSpecified')}
                </Text>
              </View>
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>{t('goals.strategicGoal')}</Text>
                <Text style={styles.infoValue}>
                  {translate(service.strategic_goal_title) || t('common.notSpecified')}
                </Text>
              </View>
            </View>
          </View>

          {/* Performance Indicators Card - MIDDLE (fixed width) */}
          <View style={[styles.statsCardFixed, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.statsCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
            <View style={styles.chartContainer}>
              {service?.achievement_percentage != null ? (
                <DonutChart percentage={service.achievement_percentage} size={168} strokeWidth={12} />
              ) : (
                <View style={{ width: 168, height: 168, justifyContent: 'center', alignItems: 'center' }}>
                  <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
            </View>
          </View>

          {/* Status Distribution Card - LEFT */}
          <View style={[styles.statsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.statsCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.statusDistribution')}</Text>
            <View style={styles.distributionContent}>
              <PieChart data={pieData} size={140} />
              <View style={styles.legendContainer}>
                <View style={styles.legendItem}>
                  <Text style={styles.legendPercentage}>{distribution.excellent ?? 0} %</Text>
                  <View style={styles.legendLabelContainer}>
                    <Text style={styles.legendLabel}>{t('status.excellent')}</Text>
                    <View style={[styles.legendBar, { backgroundColor: '#2ca02c' }]} />
                  </View>
                </View>
                <View style={styles.legendItem}>
                  <Text style={styles.legendPercentage}>{distribution.good ?? 0} %</Text>
                  <View style={styles.legendLabelContainer}>
                    <Text style={styles.legendLabel}>{t('status.good')}</Text>
                    <View style={[styles.legendBar, { backgroundColor: '#ff7f0e' }]} />
                  </View>
                </View>
                <View style={styles.legendItem}>
                  <Text style={styles.legendPercentage}>{distribution.needs_improvement ?? 0} %</Text>
                  <View style={styles.legendLabelContainer}>
                    <Text style={styles.legendLabel}>{t('status.needsImprovement')}</Text>
                    <View style={[styles.legendBar, { backgroundColor: '#d62728' }]} />
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Details Section (التفاصيل) */}
        {(service.service_fees || service.requirements_conditions || service.steps_to_obtain ||
          service.delivery_channels || service.target_customers || service.service_limitations ||
          service.service_hours || service.additional_info) && (
          <View style={[styles.detailsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.detailsTitle, { color: themeColors.textPrimary }]}>{t('services.details')}</Text>

            {service.service_fees ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.serviceFees')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.service_fees}</Text>
              </View>
            ) : null}

            {service.requirements_conditions ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.requirementsConditions')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.requirements_conditions}</Text>
              </View>
            ) : null}

            {service.steps_to_obtain ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.stepsToObtain')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.steps_to_obtain}</Text>
              </View>
            ) : null}

            {service.delivery_channels ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.deliveryChannels')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.delivery_channels}</Text>
              </View>
            ) : null}

            {service.target_customers ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.targetCustomers')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.target_customers}</Text>
              </View>
            ) : null}

            {service.service_limitations ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.serviceLimitations')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.service_limitations}</Text>
              </View>
            ) : null}

            {service.service_hours ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.serviceHours')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.service_hours}</Text>
              </View>
            ) : null}

            {service.additional_info ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.additionalInfo')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.additional_info}</Text>
              </View>
            ) : null}
          </View>
        )}

        {/* Bottom Section Grid - KPIs (2 cols) + Comments (1 col) */}
        <View style={[styles.bottomGrid, isSmallScreen && styles.bottomGridSmall]}>
          {/* Comments Section (1 column) - LEFT - Unified with GoalDetailScreen */}
          <View style={[styles.commentsColumn, isSmallScreen && styles.commentsColumnSmall]}>
            <CommentSection
              title={t('comments.title')}
              comments={comments}
              showAddComment={showAddComment}
              editingComment={editingComment}
              hideAddButton={!canComment}
              onAddClick={() => setShowAddComment(true)}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              onSubmitComment={handleCommentSubmit}
              onCancelComment={handleCommentCancel}
            />
          </View>

          {/* KPIs Section (2 columns) - RIGHT */}
          <View style={[styles.kpisSection, isSmallScreen && styles.kpisSectionSmall]}>
            <View style={[styles.kpisSectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              {/* Header */}
              <View style={styles.kpisSectionHeader}>
                <Text style={[styles.kpisSectionTitle, { color: themeColors.textPrimary }]}>{t('services.serviceLinks')}</Text>
              </View>

              {/* KPIs Tab Header */}
              <View style={styles.kpisTabHeader}>
                {!isViewOnly && (
                  <TouchableOpacity
                    style={styles.addKpiButton}
                    onPress={() => navigation.navigate('AddService', { serviceId: id, scrollToKpi: true })}
                  >
                    <PlusIcon size={13} color="#414042" />
                    <Text style={styles.addKpiButtonText}>{t('kpis.addKPI')}</Text>
                  </TouchableOpacity>
                )}
                <View style={styles.kpisTabTitleContainer}>
                  <Text style={styles.kpisTabTitle}>{t('kpis.performanceIndicators')}</Text>
                </View>
              </View>

              {/* KPIs Horizontal Scroll with RTL transform */}
              <View style={styles.rtlWrapper}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={true}
                  contentContainerStyle={styles.kpisScrollContent}
                  style={styles.horizontalScrollRTL}
                >
                  {kpis.map((kpi) => {
                    const achievement = kpi.achievement_percentage;
                    const achievedColor = achievement >= 80 ? '#2ca02c' : achievement >= 50 ? '#EAB308' : '#EF4444';
                    return (
                      <View key={kpi.id} style={styles.kpiCardWrapper}>
                        <TargetAchievementCard
                          title={translate(kpi.name)}
                          achieved={kpi.latest_value || '0'}
                          target={kpi.target_value}
                          unit={kpi.unit === 'percentage' ? '%' : translate(kpi.unit === 'count' ? 'عدد' : kpi.unit)}
                          achievedColor={achievedColor}
                          onViewDetails={() => navigation.navigate('KPIDetail', { id: kpi.id })}
                        />
                      </View>
                    );
                  })}
                  {kpis.length === 0 && (
                    <View style={styles.kpiCardWrapper}>
                      <Text style={styles.emptyStateText}>{t('kpis.noKPIs')}</Text>
                    </View>
                  )}
                </ScrollView>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDelete}
        entityName={service?.name || ''}
        fetchImpact={async () => {
          const response = await servicesApi.deletionImpact(id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  contentSmall: {
    padding: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#EF4444',
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 24,
  },
  breadcrumbSmall: {
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: 16,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  // Header Section
  headerSection: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
  },
  headerContent: {
    flex: 1,
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-end',
    textAlign: 'right',
  },
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 26,
  },
  pageDescription: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 24,
  },
  headerActions: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 20,
  },
  actionButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    minHeight: 32,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  actionButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    lineHeight: 20,
  },
  // Stats Row
  statsRow: {
    flexDirection: 'row-reverse',
    gap: 20,
    marginBottom: 24,
  },
  statsRowSmall: {
    flexDirection: 'column',
    gap: 16,
  },
  statsCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    overflow: 'hidden',
  },
  statsCardFixed: {
    width: 280,
    minWidth: 280,
    flexGrow: 0,
    flexShrink: 0,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 16,
    paddingVertical: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  statsCardTitle: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 24,
    marginBottom: 20,
  },
  // Basic Info Card
  infoItemsContainer: {
    gap: 24,
  },
  infoItem: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 8,
  },
  infoLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#44546A',
    textAlign: 'right',
    lineHeight: 20,
  },
  infoValue: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 24,
  },
  // Chart Container
  chartContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  // Distribution Content
  distributionContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  legendContainer: {
    gap: 24,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    width: 80,
  },
  legendBar: {
    width: 8,
    height: 32,
    borderRadius: 2,
  },
  legendLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    lineHeight: 14,
  },
  legendPercentage: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#303030',
  },
  // Details Section
  detailsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 20,
    marginBottom: 24,
    gap: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  detailsTitle: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 24,
  },
  detailItem: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 4,
  },
  detailItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
  },
  detailItemLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  detailItemValue: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#44546A',
    textAlign: 'right',
    lineHeight: 14,
  },
  // Bottom Grid (2-column layout)
  bottomGrid: {
    flexDirection: 'row',
    gap: 20,
  },
  bottomGridSmall: {
    flexDirection: 'column-reverse',
    gap: 16,
  },
  // Comments Column - Unified with GoalDetailScreen
  commentsColumn: {
    flex: 1,
  },
  commentsColumnSmall: {
    flex: 0,
  },
  // KPIs Section (Right column - 2x width)
  kpisSection: {
    flex: 2,
  },
  kpisSectionSmall: {
    flex: 0,
  },
  kpisSectionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    overflow: 'hidden',
  },
  kpisSectionHeader: {
    marginBottom: 24,
  },
  kpisSectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    width: '100%',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  kpisTabHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 20,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
  },
  addKpiButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 5.5,
    minHeight: 32,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  addKpiButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  kpisTabTitleContainer: {
    flex: 1,
    alignItems: 'flex-end',
    width: '100%',
  },
  kpisTabTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  // KPIs Horizontal Scroll - RTL wrapper
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  horizontalScrollRTL: {
    flexGrow: 0,
  },
  kpisScrollContent: {
    flexDirection: 'row',
    gap: 24,
    paddingHorizontal: 8,
    paddingBottom: 12,
  },
  kpiCardWrapper: {
    width: 280,
    transform: [{ scaleX: -1 }],
  },
  // Empty State
  emptyState: {
    flex: 1,
    padding: 24,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'center',
    padding: 20,
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 500,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 12,
    lineHeight: 26,
  },
  modalMessage: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    marginBottom: 16,
    lineHeight: 24,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    minHeight: 100,
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row-reverse',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCancelButtonText: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#64748B',
    lineHeight: 20,
  },
  modalRejectButton: {
    flex: 1,
    backgroundColor: '#EF4444',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalRejectButtonDisabled: {
    opacity: 0.5,
  },
  modalRejectButtonText: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
    lineHeight: 20,
  },
});
